// Pills + duration column wrapper with responsive project-pill tiers (list rows).
import { useMemo, useRef } from 'react';
import { Box } from '@mui/material';
import TaskProjectPills from '@/features/tasks/components/atoms/TaskProjectPills';
import TaskContextPills from '@/features/tasks/components/atoms/TaskContextPills';
import { TaskRowProjectPillTierMeasurer } from '@/features/tasks/components/atoms/TaskRowProjectPillTierMeasurer';
import { useTaskRowProjectPillTier } from '@/features/tasks/hooks/useTaskRowProjectPillTier';
import { buildTaskRowPillsColSx } from '@/components/ui/buildTaskRowTitleTrailingSx';

/**
 * @param {{
 *   visibleProjects: string[],
 *   projectDisplayMap: Map<string, string>,
 *   projectColorMap?: Map<string, number>,
 *   allContexts: string[],
 *   contextDisplayMap: Map<string, string>,
 *   denseChips: boolean,
 *   isExecutionView: boolean,
 * }} props
 */
export default function TaskRowPillsCol({
  visibleProjects,
  projectDisplayMap,
  projectColorMap,
  allContexts,
  contextDisplayMap,
  denseChips,
  isExecutionView,
}) {
  const pillsColRef = useRef(null);
  const contextPillsRef = useRef(null);
  const measureRef = useRef(null);

  const hasProjectPills = (visibleProjects?.length ?? 0) > 0;
  const hasContextPills =
    !isExecutionView && (allContexts?.length ?? 0) > 0;

  const displayNames = useMemo(
    () =>
      (visibleProjects ?? []).map(
        (p) =>
          projectDisplayMap.get(p) ||
          projectDisplayMap.get(p.toLowerCase()) ||
          p,
      ),
    [visibleProjects, projectDisplayMap],
  );

  const measureKey = `${denseChips}:${displayNames.join('|')}`;
  const tier = useTaskRowProjectPillTier(
    pillsColRef,
    contextPillsRef,
    measureRef,
    hasProjectPills,
    measureKey,
  );

  return (
    <Box
      ref={pillsColRef}
      className="task-item__pills-col"
      data-testid="task-item__pills-col"
      sx={buildTaskRowPillsColSx()}
    >
      <Box
        ref={measureRef}
        aria-hidden
        sx={{
          position: 'fixed',
          left: -10000,
          top: 0,
          pointerEvents: 'none',
          visibility: 'hidden',
        }}
      >
        <TaskRowProjectPillTierMeasurer
          displayNames={displayNames}
          dense={denseChips}
        />
      </Box>
      {hasProjectPills ? (
        <TaskProjectPills
          allProjects={visibleProjects}
          projectDisplayMap={projectDisplayMap}
          projectColorMap={projectColorMap}
          dense={denseChips}
          displayTier={tier}
        />
      ) : null}
      {hasContextPills ? (
        <Box
          ref={contextPillsRef}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
        >
          <TaskContextPills
            allContexts={allContexts}
            contextDisplayMap={contextDisplayMap}
            dense={denseChips}
          />
        </Box>
      ) : null}
    </Box>
  );
}
